import { apiClient } from "./api";

// Analytics types
export interface DashboardOverview {
  userStats: {
    total: number;
    newThisMonth: number;
    activeThisMonth: number;
    growthRate: number;
    creators: number;
    admins: number;
  };
  campaignStats: {
    total: number;
    active: number;
    draft: number;
    archived: number;
    validated: number;
    avgResponsesPerCampaign: number;
  };
  engagementStats: {
    totalResponses: number;
    totalComments: number;
    totalLikes: number;
    avgEngagementPerUser: number;
    responseRate: number;
  };
  platformStats: {
    totalActivities: number;
    todayActivities: number;
    weeklyGrowth: number;
    monthlyGrowth: number;
  };
}

export interface UserAnalytics {
  growthData: Array<{
    date: string;
    newUsers: number;
    totalUsers: number;
  }>;
  userTypes: Array<{
    type: string;
    count: number;
    percentage: number;
  }>;
  topUsers: Array<{
    id: string;
    name: string;
    email: string;
    campaignsCreated: number;
    responsesGiven: number;
    commentsPosted: number;
    totalEngagement: number;
    joinedDate: string;
    lastActive: string;
  }>;
  userActivity: Array<{
    date: string;
    registrations: number;
    logins: number;
    activities: number;
  }>;
}

export interface CampaignAnalytics {
  statusDistribution: Array<{
    status: string;
    count: number;
    percentage: number;
  }>;
  performanceMetrics: Array<{
    id: string;
    title: string;
    creator: string;
    responses: number;
    comments: number;
    likes: number;
    engagementRate: number;
    validationScore: number;
    daysActive: number;
    status: string;
  }>;
  categoryBreakdown: Array<{
    category: string;
    count: number;
    avgResponses: number;
    avgEngagement: number;
  }>;
  campaignGrowth: Array<{
    date: string;
    created: number;
    published: number;
    completed: number;
  }>;
}

export interface EngagementAnalytics {
  responseTrends: Array<{
    date: string;
    responses: number;
    comments: number;
    likes: number;
  }>;
  questionTypePerformance: Array<{
    type: string;
    count: number;
    avgResponses: number;
    completionRate: number;
  }>;
  sentimentAnalysis: Array<{
    sentiment: string;
    count: number;
    percentage: number;
  }>;
  peakActivityTimes: Array<{
    hour: number;
    activities: number;
    responses: number;
    comments: number;
  }>;
}

export interface ConversionFunnel {
  stages: Array<{
    stage: string;
    count: number;
    conversionRate: number;
    dropoffRate: number;
  }>;
  userJourney: Array<{
    step: string;
    users: number;
    percentage: number;
  }>;
  timeToConversion: {
    avgDaysToFirstCampaign: number;
    avgDaysToFirstResponse: number;
    avgDaysToFirstComment: number;
  };
}

// Analytics API service
export const analyticsApi = {
  // Get dashboard overview
  async getDashboardOverview(): Promise<DashboardOverview> {
    return apiClient.get<DashboardOverview>("/analytics/dashboard-overview");
  },

  // Get user analytics
  async getUserAnalytics(): Promise<UserAnalytics> {
    return apiClient.get<UserAnalytics>("/analytics/users");
  },

  // Get campaign analytics
  async getCampaignAnalytics(): Promise<CampaignAnalytics> {
    return apiClient.get<CampaignAnalytics>("/analytics/campaigns");
  },

  // Get engagement analytics
  async getEngagementAnalytics(): Promise<EngagementAnalytics> {
    return apiClient.get<EngagementAnalytics>("/analytics/engagement");
  },

  // Get conversion funnel
  async getConversionFunnel(): Promise<ConversionFunnel> {
    return apiClient.get<ConversionFunnel>("/analytics/conversion-funnel");
  },

  // Debug: Get total user count
  async getUserCount(): Promise<{ totalUsers: number }> {
    return apiClient.get<{ totalUsers: number }>("/analytics/debug/user-count");
  },

  // Debug: Get growth data details
  async getGrowthDataDebug(): Promise<{
    usersBeforePeriod: number;
    rawGrowthData: Array<{
      date: string;
      newUsers: number;
      totalUsers: number;
    }>;
    processedData: Array<{
      date: string;
      newUsers: number;
      totalUsers: number;
    }>;
    currentTotalUsers: number;
    thirtyDaysAgo: string;
  }> {
    return apiClient.get<{
      usersBeforePeriod: number;
      rawGrowthData: Array<{
        date: string;
        newUsers: number;
        totalUsers: number;
      }>;
      processedData: Array<{
        date: string;
        newUsers: number;
        totalUsers: number;
      }>;
      currentTotalUsers: number;
      thirtyDaysAgo: string;
    }>("/analytics/debug/growth-data");
  },
};
